import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { getDataSetEfficiency } from "./efficiencyPoints";
import { getDataSetTDH } from "./producedTDH";
import { getDataSetRequiredTDH } from "./requiredTDH";
import { getDataSetBrakeHP } from "./getBrakeHPPoints";
import { getDataSetNPSHR } from "./npshrPoints";
import { getDataSetNPSHA } from "./npshaPoints";
import { getDataSetTargetNPSHR } from "./targetNPSHRPoints";
import type { SinglePumpChartData } from "../d";
import { buildSinglePumpChartContext } from "../_common/getContext";
import { convert, type ConvertableUnit } from "@/libs/units";
import { getDensityCorrectionRatio } from "../_common/densityCorrection";

export const useSinglePumpChartData = (
  scenarioCalculations: ScenarioCalculations,
  targetLiquidFlowUnit: ConvertableUnit,
  targetLargeLengthUnit: ConvertableUnit,
  targetPressureUnit: ConvertableUnit,
  targetPowerUnit: ConvertableUnit
): SinglePumpChartData | undefined => {
  // We only care about aggregate pump data when using this
  // Primary pump can dictate a few things if needed
  const pump = scenarioCalculations.primaryPump.value;
  if (!pump) return undefined;

  const context = buildSinglePumpChartContext(
    scenarioCalculations,
    targetLiquidFlowUnit,
    targetLargeLengthUnit,
    targetPressureUnit,
    targetPowerUnit
  );
  const isRateCorrected =
    !!scenarioCalculations.descendingOrderPumps.value.find(
      p => [0, 1].indexOf(p.rate_correction_ratio) === -1
    );
  if (!context) {
    return undefined;
  } else {
    // Let's get our required target value here. TDH if ESP, pressure if HPS.
    // This is needed for the density correction.
    const scenarioRequiredTargetTDH =
      scenarioCalculations.pumpCalculations.aggregate.requiredTotalDynamicHeadFt
        .value;
    const scenarioRequiredTargetPressurePSIA =
      scenarioCalculations.pumpCalculations.aggregate.differentialPressurePSIA
        .value;
    const scenarioRequiredTargetBrakeHP =
      scenarioCalculations.pumpCalculations.aggregate.requiredPumpBrakeHP.value;
    const {
      pumpIntakeFlowRateBBLD,
      ascendingOrderPumps,
      gasHeadRerateRatio,
      pumpWearHeadCorrectionRatio,
      pumpWearBHPCorrectionRatio,
      dischargeFluidDensityLBMFT3,
      frequencyRatio
    } = context;
    const densityCorrectionRatios = getDensityCorrectionRatio(
      pumpIntakeFlowRateBBLD,
      ascendingOrderPumps,
      gasHeadRerateRatio,
      pumpWearHeadCorrectionRatio,
      pumpWearBHPCorrectionRatio,
      dischargeFluidDensityLBMFT3,
      frequencyRatio,
      scenarioRequiredTargetTDH,
      scenarioRequiredTargetPressurePSIA,
      scenarioRequiredTargetBrakeHP
    );

    const brakeHPDataSet = getDataSetBrakeHP(
      context,
      densityCorrectionRatios.densityCorrectionBHPRatio
    );
    // Apply density correction to the produced TDH curve so it intersects the
    // required TDH at the required flow/pressure target. This creates a more realistic
    // produced TDH curve that accounts for fluid density effects, which is especially
    // important for HPS scenarios where the required TDH is a pressure target.
    const tdhProducedDataSet = getDataSetTDH(
      context,
      densityCorrectionRatios.densityCorrectionTDHRatio,
      densityCorrectionRatios.densityCorrectionPressureRatio
    );

    const tdhRequiredDataSet = getDataSetRequiredTDH(
      context,
      densityCorrectionRatios.densityCorrectionTDHRatio
    );

    const npshrDataSet = getDataSetNPSHR(context);
    // Use original points from brakeHP and tdh produced to calculate efficiency
    // so we don't calculate an efficiency curve based on converted data
    const efficiencyDataSet = getDataSetEfficiency(
      context,
      brakeHPDataSet.originalPoints,
      tdhProducedDataSet.originalPointsForEfficiency
    );
    const { baseXUnit, targetXUnit } = context;
    return {
      context,
      dataSets: {
        efficiency: efficiencyDataSet,
        tdhProduced: tdhProducedDataSet,
        tdhRequired: tdhRequiredDataSet,
        bhp: brakeHPDataSet,
        npsha: getDataSetNPSHA(context),
        npshr: npshrDataSet,
        targetNpshr: getDataSetTargetNPSHR(context, npshrDataSet.originalPoints)
      },
      rorMin: Math.round(convert(context.rorMin, baseXUnit, targetXUnit)) || 0,
      rorMax: Math.round(convert(context.rorMax, baseXUnit, targetXUnit)) || 0,
      rorBep: Math.round(convert(context.rorBep, baseXUnit, targetXUnit)) || 0,
      // The OP threshold is the pump discharge flow rate
      operatingPointThreshold: Math.round(
        convert(context.pumpDischargeFlowRateBBLD, baseXUnit, targetXUnit)
      ),
      isRateReduced: brakeHPDataSet.rateReducedPumpIds.length > 0,
      isRateCorrected,
      targetXUnitLabel: context.targetXUnitLabel,
      targetYUnitLabel: context.targetYUnitLabel,
      targetNpshrYUnitLabel: context.targetNpshYUnitLabel
    };
  }
};
